import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import { pdfToArrayBuffer, validatePdfFile, getPdfPageCount } from '../../utils/pdfUtils';
import { Document } from '../../types';

const ALGEMENE_VOORWAARDE_LOADED_KEY = 'wensing-app-av-loaded';

export function AlgemeneVoorwaarde() {
  const { state, dispatch } = useApp();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const loadDefaultAlgemeneVoorwaarde = async () => {
      // Only load default if not already loaded and no existing algemene voorwaarde
      if (localStorage.getItem(ALGEMENE_VOORWAARDE_LOADED_KEY) || state.algemeneVoorwaarde) {
        return;
      }

      try {
        console.log('Loading default Algemene Voorwaarde...');
        const response = await fetch('/templates/FWa_Algemene_voorwaarden.pdf');

        if (!response.ok) {
          console.warn('Could not load default Algemene Voorwaarde');
          return;
        }

        const pdfData = await response.arrayBuffer();
        const pageCount = await getPdfPageCount(pdfData);

        const document: Document = {
          id: 'algemene-voorwaarde-default',
          name: 'FWa Algemene voorwaarden',
          pdfData,
          pageCount,
          uploadedAt: new Date(),
        };

        dispatch({ type: 'SET_ALGEMENE_VOORWAARDE', payload: document });
        localStorage.setItem(ALGEMENE_VOORWAARDE_LOADED_KEY, 'true');
        console.log('Default Algemene Voorwaarde loaded successfully');
      } catch (error) {
        console.error('Error loading default Algemene Voorwaarde:', error);
      }
    };

    loadDefaultAlgemeneVoorwaarde();
  }, [dispatch, state.algemeneVoorwaarde]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const validationError = validatePdfFile(file);

    if (validationError) {
      dispatch({ type: 'SET_ERROR', payload: validationError });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const pdfData = await pdfToArrayBuffer(file);
      const pageCount = await getPdfPageCount(pdfData);

      const document: Document = {
        id: 'algemene-voorwaarde-' + Date.now(),
        name: file.name,
        pdfData,
        pageCount,
        uploadedAt: new Date(),
      };

      dispatch({ type: 'SET_ALGEMENE_VOORWAARDE', payload: document });
      setIsOpen(false);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Fout bij uploaden Algemene Voorwaarde'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const handleRemove = () => {
    if (window.confirm('Weet je zeker dat je de Algemene Voorwaarde wilt verwijderen?')) {
      dispatch({ type: 'SET_ALGEMENE_VOORWAARDE', payload: null });
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="text-sm text-gray-600 dark:text-white hover:text-gray-800 dark:hover:text-blue-400 px-3 py-1 rounded border border-gray-300 dark:border-blue-600 hover:border-gray-400 dark:hover:border-blue-400 transition-colors"
      >
        Algemene Voorwaarde
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-black border dark:border-blue-600 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Algemene Voorwaarde
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-blue-400"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {state.algemeneVoorwaarde ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-600 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-green-700 dark:text-green-300 font-medium">
                  {state.algemeneVoorwaarde.name}
                </span>
              </div>
              <p className="text-green-600 dark:text-green-400 text-sm mt-1">
                Geüpload op {state.algemeneVoorwaarde.uploadedAt.toLocaleDateString()}
              </p>
            </div>

            <div className="flex justify-between">
              <button
                onClick={handleRemove}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Verwijderen
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
              >
                Sluiten
              </button>
            </div>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                : 'border-gray-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-400'
              }
            `}
          >
            <input {...getInputProps()} />
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-gray-600 dark:text-white mb-1">
              {isDragActive ? 'Drop het PDF bestand hier...' : 'Upload een PDF bestand'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Sleep een PDF bestand hiernaartoe of klik om te selecteren
            </p>
          </div>
        )}
      </div>
    </div>
  );
}